import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Ticket, ShoppingBag, Calendar, Briefcase, Plus, X } from "lucide-react";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";

/**
 * Speed-dial flottant en bas à droite, type "Material FAB".
 * Permet un accès permanent à : Billetterie / Boutique / Calendrier / Espace Business.
 */
export default function SportSpeedDial({ ticketingUrl }) {
  const [open, setOpen] = useState(false);

  const actions = [
    {
      label: "Billetterie",
      icon: Ticket,
      href: ticketingUrl || "https://billetterie.istreshandball.com/",
      external: true,
      color: SECONDARY,
      testid: "fab-billetterie",
    },
    {
      label: "Boutique",
      icon: ShoppingBag,
      to: "/sport/boutique",
      color: "#ff7e35",
      testid: "fab-boutique",
    },
    {
      label: "Calendrier",
      icon: Calendar,
      to: "/sport/calendrier",
      color: "#0ea5e9",
      testid: "fab-calendrier",
    },
    {
      label: "Espace Business",
      icon: Briefcase,
      to: "/sport/partenaires",
      color: PRIMARY,
      testid: "fab-business",
    },
  ];

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3" data-testid="sport-speed-dial">
      <AnimatePresence>
        {open && actions.map((a, i) => {
          const Icon = a.icon;
          const buttonClasses = "w-12 h-12 rounded-full flex items-center justify-center bg-white shadow-2xl hover:scale-110 transition border-2";
          const iconStyle = { color: a.color };
          const borderStyle = { borderColor: a.color };
          return (
            <motion.div
              key={a.label}
              initial={{ opacity: 0, x: 32, scale: 0.6 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 32, scale: 0.6 }}
              transition={{ duration: 0.22, delay: i * 0.04 }}
              className="flex items-center gap-3"
            >
              <span className="bg-black/85 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-xl whitespace-nowrap">
                {a.label}
              </span>
              {a.external ? (
                <a
                  href={a.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={a.testid}
                  onClick={() => setOpen(false)}
                  className={buttonClasses}
                  style={borderStyle}
                  aria-label={a.label}
                >
                  <Icon size={18} style={iconStyle}/>
                </a>
              ) : (
                <Link
                  to={a.to}
                  data-testid={a.testid}
                  onClick={() => setOpen(false)}
                  className={buttonClasses}
                  style={borderStyle}
                  aria-label={a.label}
                >
                  <Icon size={18} style={iconStyle}/>
                </Link>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
      <button
        data-testid="fab-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-16 h-16 rounded-full flex items-center justify-center text-white shadow-[0_15px_40px_rgba(245,166,35,0.55)] hover:shadow-[0_20px_50px_rgba(245,166,35,0.7)] transition"
        style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}
        aria-label={open ? "Fermer le menu rapide" : "Ouvrir le menu rapide"}
      >
        <motion.div animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.25 }}>
          {open ? <X size={26}/> : <Plus size={26}/>}
        </motion.div>
      </button>
    </div>
  );
}
